import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: 'SHS road, Kalyan Nagar, Karnataka 577102',
      link: 'https://maps.google.com/?q=SHS+road,+Kalyan+Nagar,+Karnataka+577102',
      color: 'bg-rose-500',
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '9481694328',
      link: 'tel:9481694328',
      color: 'bg-sky-400',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'sacredheartckm@gmail.com',
      link: 'mailto:sacredheartckm@gmail.com',
      color: 'bg-gradient-to-br from-orange-400 to-amber-500',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      content: 'Mon - Sat: 8:00 AM - 4:00 PM',
      link: null,
      color: 'bg-gradient-to-br from-teal-400 to-green-500',
    },
  ];

  return (
    <div className="min-h-screen pt-28 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <span className="font-accent text-xl text-purple-500 inline-block -rotate-2 mb-2">
              Contact Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 bg-clip-text text-transparent mb-6">
              We'd Love to Hear From You
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Have questions about admissions, programs, or anything else? Reach out to us and we'll be happy to help!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`contact-info-${info.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {info.link ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="block bg-white p-6 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group"
                  >
                    <div className={`w-14 h-14 ${info.color} rounded-2xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                      <info.icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-800 mb-1">
                      {info.title}
                    </h3>
                    <p className="font-body text-slate-600 group-hover:text-rose-500 transition-colors">
                      {info.content}
                    </p>
                  </a>
                ) : (
                  <div className="bg-white p-6 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
                    <div className={`w-14 h-14 ${info.color} rounded-2xl flex items-center justify-center mb-4`}>
                      <info.icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-800 mb-1">
                      {info.title}
                    </h3>
                    <p className="font-body text-slate-600">
                      {info.content}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-xl text-rose-500 inline-block -rotate-2 mb-2">
                Send a Message
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-800 mb-8">
                Get in Touch
              </h2>

              <div className="bg-white p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
                {isSubmitted ? (
                  <div className="text-center py-8" data-testid="contact-form-success">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-slate-800 mb-2">
                      Message Sent!
                    </h3>
                    <p className="font-body text-slate-600">
                      Thank you for reaching out. We'll get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-body font-semibold text-slate-700 mb-2">
                          Your Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 font-body transition-all"
                          placeholder="John Doe"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div>
                        <label className="block font-body font-semibold text-slate-700 mb-2">
                          Phone Number *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 font-body transition-all"
                          placeholder="Your phone number"
                          data-testid="contact-input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 font-body transition-all"
                        placeholder="john@example.com"
                        data-testid="contact-input-email"
                      />
                    </div>

                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 font-body transition-all"
                        placeholder="What's this about?"
                        data-testid="contact-input-subject"
                      />
                    </div>

                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-400/20 font-body transition-all resize-none"
                        placeholder="Your message..."
                        data-testid="contact-input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-4 bg-rose-500 text-white font-body font-bold text-lg rounded-full transition-all duration-300 hover:-translate-y-1 hover:bg-rose-600 hover:shadow-lg hover:shadow-rose-500/30 active:translate-y-0 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-xl text-rose-500 inline-block -rotate-2 mb-2">
                Find Us
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-800 mb-8">
                Our Location
              </h2>

              <div className="bg-white p-4 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
                <div className="rounded-[1.5rem] overflow-hidden h-[400px] lg:h-[500px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3865.123456789!2d75.123456!3d14.123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sSacred%20Heart%20School%2C%20SHS%20road%2C%20Kalyan%20Nagar%2C%20Karnataka%20577102!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sacred Heart School Location"
                    data-testid="google-map"
                  ></iframe>
                </div>
              </div>

              {/* Quick Contact */}
              <div className="mt-8 bg-gradient-to-r from-rose-500 to-amber-400 p-6 rounded-[2rem] text-white">
                <h3 className="font-heading text-xl font-semibold mb-4">
                  Prefer to chat directly?
                </h3>
                <p className="font-body mb-4 text-white/90">
                  Reach out to us on WhatsApp for quick responses!
                </p>
                <a
                  href="https://wa.me/919481694328?text=Hello!%20I%20have%20a%20question%20about%20Sacred%20Heart%20School."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-rose-500 font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:scale-95"
                  data-testid="contact-whatsapp-cta"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
